import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import * as Ramda from 'ramda';
import { FormProvider, useForm } from 'react-hook-form';
import { DialogActions, DialogContent, Grid } from '@mui/material';
import CbButton from '../../../../../../../../../components/Buttons/CbButton';
import {
  ControlledLanguageCurrencyField,
  ControlledTextField,
  stripSpecialChars,
} from './shared/common';
import type { PrimeXProductType } from '../../../../../../../../../types';
import { ProductTypes } from '../../../../../../../../../types';
import type { Languages } from '../../../../../../../../../i18n/i18n.language-config';
import { Show } from '../../../../../../../../../components/Show';

type ModalProps = {
  data: {
    productType: PrimeXProductType;
    defaultValues: DCFormValues;
    language: Languages;
    onSubmit: (formValues: DCFormValues) => void;
  };
  close: () => void;
};

const DefenseCounselModal = ({ data: modalProps, close }: ModalProps) => {
  const formMethods = useForm<DCFormValues>({
    defaultValues: modalProps.defaultValues,
    resolver: yupResolver(schema),
  });

  const isOtherRateVisible = modalProps.productType !== ProductTypes.prime_one;

  return (
    <FormProvider {...formMethods}>
      <form
        noValidate
        onSubmit={formMethods.handleSubmit(
          Ramda.compose(close, modalProps.onSubmit)
        )}
      >
        <DialogContent style={{ padding: '2rem', paddingTop: 0 }}>
          <Grid container spacing={1}>
            <Grid item xs={isOtherRateVisible ? 12 : 6}>
              <ControlledTextField name="name" label="Firm Name" required />
            </Grid>

            <Grid item xs={6}>
              <ControlledLanguageCurrencyField
                name="partnerRate"
                label="Partner hourly rate"
                language={modalProps.language}
                required
              />
            </Grid>

            <Grid item xs={6}>
              <ControlledLanguageCurrencyField
                name="associateRate"
                label="Associate hourly rate"
                language={modalProps.language}
                required
              />
            </Grid>

            <Grid item xs={6}>
              <ControlledLanguageCurrencyField
                name="paralegalRate"
                label="Paralegal hourly rate"
                language={modalProps.language}
                required
              />
            </Grid>

            <Show when={isOtherRateVisible}>
              <Grid item xs={6}>
                <ControlledLanguageCurrencyField
                  name="otherRate"
                  label="Other hourly rate"
                  language={modalProps.language}
                  required
                />
              </Grid>
            </Show>
          </Grid>
        </DialogContent>

        <DialogActions>
          <CbButton styleName="cancel" onClick={close}>
            Cancel
          </CbButton>
          <CbButton styleName="ctaButton" type="submit">
            Save
          </CbButton>
        </DialogActions>
      </form>
    </FormProvider>
  );
};

export default DefenseCounselModal;

export const DefenseCounselModalConfig = {
  DefenseCounselModal: {
    component: DefenseCounselModal,
    config: {
      title: 'Add/Edit Defense Counsel',
      fullWidth: true,
      maxWidth: 'sm',
    },
  },
};

const schema = Yup.object().shape({
  name: Yup.string().required('Firm Name is required'),
  partnerRate: Yup.number().transform(stripSpecialChars),
  associateRate: Yup.number().transform(stripSpecialChars),
  paralegalRate: Yup.number().transform(stripSpecialChars),
  otherRate: Yup.number().transform(stripSpecialChars),
});

export type DCFormValues = {
  name: string;
  partnerRate: number;
  associateRate: number;
  paralegalRate: number;
  otherRate?: number;
};
